import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { ApiError, post } from "../lib/api.js";
import { Button, Field, Input } from "../components/ui.js";
import { Mark } from "../components/icons.js";

/**
 * `/establecer-contrasena?token=…` — the one-time link printed by `create-operator`,
 * `reissue-operator-link` (ADR 0017) and `issue-user-link` (a laboratory user's reset). Public
 * route; the token is the only credential.
 */
export const MIN_PASSWORD_LENGTH = 10;

export function SetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const mismatch = confirm.length > 0 && confirm !== password;
  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (tooShort || mismatch) return;
    setBusy(true);
    setError(null);
    try {
      await post("setPassword", { token, password });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError) setError(err.problem.detail ?? err.problem.title);
      else setError("No se pudo conectar con el servidor. Revisa la conexión.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="screen-center green">
      <form className="login" onSubmit={onSubmit} noValidate>
        <div className="brand">
          <Mark />
          <span className="text">
            <span className="name">Probeta Lab</span>
            <br />
            <span className="sub">Sistema del laboratorio</span>
          </span>
        </div>
        {done ? (
          <>
            <div>
              <h1 className="h1">Contraseña guardada</h1>
              <p className="muted">Ya puedes iniciar sesión con tu correo y la contraseña que acabas de elegir.</p>
            </div>
            <Link to="/iniciar-sesion" className="btn btn-primary btn-lg btn-block">
              Ir a iniciar sesión
            </Link>
          </>
        ) : !token ? (
          <div>
            <h1 className="h1">Liga incompleta</h1>
            <p className="muted">Abre la liga completa que recibiste; si ya venció, pide una nueva.</p>
          </div>
        ) : (
          <>
            <div>
              <h1 className="h1">Elige tu contraseña</h1>
              <p className="muted">Mínimo {MIN_PASSWORD_LENGTH} caracteres. Esta liga sirve una sola vez.</p>
            </div>
            <Field label="Contraseña nueva" error={tooShort ? `Usa al menos ${MIN_PASSWORD_LENGTH} caracteres.` : null}>
              <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus className="lg" aria-invalid={tooShort ? true : undefined} />
            </Field>
            <Field label="Confirmar contraseña" error={mismatch ? "Las contraseñas no coinciden." : error}>
              <Input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="lg" aria-invalid={mismatch || error ? true : undefined} />
            </Field>
            <Button type="submit" variant="primary" size="lg" block busy={busy} disabled={!password || !confirm || tooShort || mismatch}>
              Guardar contraseña
            </Button>
          </>
        )}
      </form>
    </div>
  );
}
